import { useState } from "react";
import { gamificationProfile, gamificationLeaderboard } from "@/mocks/listenerGamification";
import { currentListenerProfile, listenerLevels } from "@/mocks/listenerLevels";
import { badgeCollection, badgeStats } from "@/mocks/listenerBadges";
import { topFansAll } from "@/mocks/topFansRanking";
import { vipUsersAll, tierConfig, vipDashboardStats } from "@/mocks/rfwVipUsers";
import { getAllClubs, getTopClubs, clubTypeColors, clubTypeLabels } from "@/mocks/rfwClubs";
import type { VipTier } from "@/mocks/rfwVipUsers";

type GamSection = "puntos" | "insignias" | "rankings" | "vip" | "clubes";

function formatCount(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

function timeAgoSimple(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Ahora";
  if (mins < 60) return `Hace ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Hace ${hrs}h`;
  return `Hace ${Math.floor(hrs / 24)}d`;
}

const vipTierOrder: VipTier[] = ["diamante", "oro", "plata"];

export default function AppCommunityGamification() {
  const [section, setSection] = useState<GamSection>("puntos");
  const [vipFilter, setVipFilter] = useState<VipTier | "todos">("todos");

  const profile = currentListenerProfile;
  const gProfile = gamificationProfile;
  const clubs = getAllClubs();
  const topClubs = getTopClubs(6);

  const filteredVips = vipFilter === "todos" ? vipUsersAll : vipUsersAll.filter((v) => v.tier === vipFilter);

  const sections = [
    { id: "puntos" as GamSection, label: "Puntos", icon: "ri-flashlight-line" },
    { id: "insignias" as GamSection, label: "Insignias", icon: "ri-award-line" },
    { id: "rankings" as GamSection, label: "Rankings", icon: "ri-trophy-line" },
    { id: "vip" as GamSection, label: "VIP", icon: "ri-vip-diamond-line" },
    { id: "clubes" as GamSection, label: "Clubes", icon: "ri-shield-star-line" },
  ];

  // Common header with points/level summary
  const HeaderSummary = () => (
    <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 border border-amber-200/60 rounded-2xl p-3.5 mb-3">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-rose-400 flex items-center justify-center shrink-0">
          <span className="text-white text-base font-bold">{profile.currentLevel}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-foreground-950">{profile.currentLevelName}</span>
            <span className="text-[10px] text-foreground-500">#{profile.globalRank} global</span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-1.5 rounded-full bg-amber-200 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-orange-400 to-rose-400" style={{ width: `${profile.progressPercent}%` }}></div>
            </div>
            <span className="text-[9px] text-foreground-400 whitespace-nowrap">{profile.currentPoints}/{profile.pointsToNextLevel} pts</span>
          </div>
          <p className="text-[9px] text-foreground-500 mt-0.5">Siguiente: {profile.nextLevelName}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-lg font-bold text-foreground-900">{formatCount(gProfile.totalPoints)}</div>
          <div className="text-[9px] text-foreground-500">pts totales</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full overflow-y-auto bg-background-50 scrollbar-none">
      <HeaderSummary />

      {/* Sub-tabs */}
      <div className="flex gap-1 px-3 mb-3 overflow-x-auto scrollbar-none">
        {sections.map((s) => (
          <button
            key={s.id}
            onClick={() => setSection(s.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
              section === s.id
                ? "bg-foreground-950 text-background-50"
                : "bg-background-100 text-foreground-500 hover:bg-background-200"
            }`}
          >
            <i className={`${s.icon} text-[12px]`}></i>
            {s.label}
          </button>
        ))}
      </div>

      <div className="px-3 pb-6">
        {/* ═══ PUNTOS - Points history & rules ═══ */}
        {section === "puntos" && (
          <div className="space-y-3">
            {/* Weekly progress */}
            <div className="bg-background-50 border border-background-200 rounded-2xl p-3.5">
              <h3 className="text-xs font-bold text-foreground-950 mb-2 flex items-center gap-1.5">
                <i className="ri-fire-line text-orange-500"></i>
                Objetivo Semanal
              </h3>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span className="text-[10px] text-foreground-600">{gProfile.stats.weeklyPoints} pts</span>
                    <span className="text-[10px] text-foreground-400">{gProfile.stats.weeklyGoal} pts</span>
                  </div>
                  <div className="h-2 rounded-full bg-background-200 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-400" style={{ width: `${Math.min((gProfile.stats.weeklyPoints / gProfile.stats.weeklyGoal) * 100, 100)}%` }}></div>
                  </div>
                </div>
                <span className="text-[10px] font-bold text-foreground-600">{gProfile.stats.streakDays} días racha</span>
              </div>
              <div className="grid grid-cols-3 gap-1.5 mt-2">
                <div className="text-center py-1.5 rounded-lg bg-amber-50">
                  <div className="text-xs font-bold text-foreground-900">{gProfile.stats.todayPoints}</div>
                  <div className="text-[8px] text-foreground-400">Hoy</div>
                </div>
                <div className="text-center py-1.5 rounded-lg bg-amber-50">
                  <div className="text-xs font-bold text-foreground-900">{gProfile.stats.greetingsSent}</div>
                  <div className="text-[8px] text-foreground-400">Saludos</div>
                </div>
                <div className="text-center py-1.5 rounded-lg bg-amber-50">
                  <div className="text-xs font-bold text-foreground-900">{gProfile.stats.contestsJoined}</div>
                  <div className="text-[8px] text-foreground-400">Concursos</div>
                </div>
              </div>
            </div>

            {/* Point rules */}
            <div className="bg-background-50 border border-background-200 rounded-2xl p-3.5">
              <h3 className="text-xs font-bold text-foreground-950 mb-2">Cómo ganar puntos</h3>
              <div className="space-y-2">
                {gProfile.rules.map((rule) => (
                  <div key={rule.id} className="flex items-center gap-2.5">
                    <div className={`w-8 h-8 rounded-lg ${rule.color} bg-opacity-15 flex items-center justify-center shrink-0`}>
                      <i className={`${rule.icon} text-white text-xs`} style={{ color: "white" }}></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-semibold text-foreground-900">{rule.action}</span>
                        <span className="text-[11px] font-bold text-foreground-700">+{rule.points}</span>
                      </div>
                      <p className="text-[9px] text-foreground-500">{rule.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent activity */}
            <div className="bg-background-50 border border-background-200 rounded-2xl p-3.5">
              <h3 className="text-xs font-bold text-foreground-950 mb-2">Actividad Reciente</h3>
              <div className="space-y-2">
                {gProfile.history.slice(0, 8).map((h) => (
                  <div key={h.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-400 shrink-0"></span>
                      <div className="min-w-0">
                        <p className="text-[10px] text-foreground-800 truncate">{h.action} · {h.station}</p>
                        <p className="text-[8px] text-foreground-400">{timeAgoSimple(h.timestamp)}</p>
                      </div>
                    </div>
                    <span className="text-[10px] font-bold text-emerald-600 shrink-0">+{h.points}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ═══ INSIGNIAS ═══ */}
        {section === "insignias" && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-foreground-950">Mis Insignias</h3>
              <span className="text-[10px] text-foreground-500">{badgeStats.earnedBadges}/{badgeStats.totalBadges} · {badgeStats.completionPercent}%</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {badgeCollection.map((badge) => (
                <div
                  key={badge.id}
                  className={`rounded-2xl p-3 border transition-all ${
                    badge.earned
                      ? `${badge.borderColor} ${badge.colorLight}`
                      : "border-background-200 bg-background-50 opacity-70"
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`w-9 h-9 rounded-lg ${badge.earned ? badge.colorLight : "bg-background-200"} flex items-center justify-center shrink-0`}>
                      <i className={`${badge.icon} ${badge.earned ? badge.textColor : "text-foreground-400"} text-base`}></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className={`text-[11px] font-bold ${badge.earned ? "text-foreground-900" : "text-foreground-500"}`}>{badge.name}</h4>
                      <p className="text-[9px] text-foreground-500 mt-0.5 leading-relaxed">{badge.description}</p>
                      {badge.earned && badge.earnedDate && (
                        <p className="text-[8px] text-emerald-600 mt-0.5">Obtenida {badge.earnedDate}</p>
                      )}
                      {!badge.earned && (
                        <div className="mt-1.5">
                          <div className="h-1 rounded-full bg-background-200 overflow-hidden">
                            <div className="h-full rounded-full bg-primary-400" style={{ width: `${badge.progress}%` }}></div>
                          </div>
                          {badge.progressLabel && <p className="text-[8px] text-foreground-400 mt-0.5">{badge.progressLabel}</p>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══ RANKINGS / TOP FANS ═══ */}
        {section === "rankings" && (
          <div className="space-y-3">
            {/* Leaderboard */}
            <div className="bg-background-50 border border-background-200 rounded-2xl p-3.5">
              <h3 className="text-xs font-bold text-foreground-950 mb-2 flex items-center gap-1.5">
                <i className="ri-trophy-line text-amber-500"></i>Top Fans Globales
              </h3>
              <div className="space-y-1.5">
                {gamificationLeaderboard.map((entry) => (
                  <div
                    key={entry.rank}
                    className={`flex items-center gap-2.5 p-2 rounded-xl transition-all ${
                      entry.rank <= 3 ? "bg-amber-50 border border-amber-200" : "bg-background-50 border border-background-200"
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                      entry.rank === 1 ? "bg-amber-400 text-white" :
                      entry.rank === 2 ? "bg-slate-400 text-white" :
                      entry.rank === 3 ? "bg-orange-400 text-white" :
                      "bg-background-200 text-foreground-500"
                    }`}>
                      {entry.rank}
                    </div>
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-background-200 shrink-0">
                      <img src={entry.photo} alt={entry.name} className="w-full h-full object-cover object-top" loading="lazy" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-bold text-foreground-900 truncate">{entry.name}</p>
                      <p className="text-[9px] text-foreground-500">{entry.username} · Nivel {entry.level}</p>
                    </div>
                    <span className="text-[11px] font-bold text-foreground-700 shrink-0">{formatCount(entry.points)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Fans by Radio */}
            <div className="bg-background-50 border border-background-200 rounded-2xl p-3.5">
              <h3 className="text-xs font-bold text-foreground-950 mb-2">Top Fans por Radio</h3>
              {["Radio Éxito FM", "Estéreo Capital", "Radio Deportiva Total"].map((radioName) => {
                const radioFans = topFansAll.filter((f) => f.radioName === radioName).slice(0, 3);
                return (
                  <div key={radioName} className="mb-2 last:mb-0">
                    <p className="text-[10px] font-semibold text-foreground-600 mb-1">{radioName}</p>
                    <div className="flex gap-1.5">
                      {radioFans.map((fan) => (
                        <div key={fan.id} className="flex items-center gap-1.5 flex-1 bg-background-100 rounded-lg p-1.5">
                          <span className="text-[9px] font-bold text-foreground-400">{fan.rank}</span>
                          <span className="text-[9px] text-foreground-800 truncate">{fan.name.split(" ")[0]}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══ VIP ═══ */}
        {section === "vip" && (
          <div className="space-y-3">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-1.5">
              <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-2.5 text-center">
                <div className="text-sm font-bold text-foreground-900">{vipDashboardStats.diamante}</div>
                <div className="text-[8px] text-foreground-500">💎 Diamante</div>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-2.5 text-center">
                <div className="text-sm font-bold text-foreground-900">{vipDashboardStats.oro}</div>
                <div className="text-[8px] text-foreground-500">🥇 Oro</div>
              </div>
              <div className="bg-gradient-to-br from-slate-50 to-gray-50 border border-slate-200 rounded-xl p-2.5 text-center">
                <div className="text-sm font-bold text-foreground-900">{vipDashboardStats.plata}</div>
                <div className="text-[8px] text-foreground-500">🥈 Plata</div>
              </div>
            </div>

            {/* Tier filter */}
            <div className="flex gap-1.5 overflow-x-auto scrollbar-none">
              {(["todos", "diamante", "oro", "plata"] as const).map((tier) => (
                <button
                  key={tier}
                  onClick={() => setVipFilter(tier)}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                    vipFilter === tier
                      ? "bg-foreground-950 text-background-50"
                      : "bg-background-100 text-foreground-500 hover:bg-background-200"
                  }`}
                >
                  {tier === "todos" ? "Todos" : tier === "diamante" ? "💎 Diamante" : tier === "oro" ? "🥇 Oro" : "🥈 Plata"}
                </button>
              ))}
            </div>

            {/* VIP users */}
            <div className="space-y-2">
              {filteredVips.slice(0, 10).map((vip) => {
                const tierCfg = tierConfig[vip.tier];
                return (
                  <div key={vip.id} className="flex items-center gap-2.5 bg-background-50 border border-background-200 rounded-2xl p-2.5">
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200">
                        <img src={vip.photoUrl} alt={vip.name} className="w-full h-full object-cover object-top" loading="lazy" />
                      </div>
                      <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full ${tierCfg.badgeBg} flex items-center justify-center border-2 border-background-50`}>
                        <i className={`${tierCfg.icon} ${tierCfg.color} text-[8px]`}></i>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[11px] font-bold text-foreground-900 truncate">{vip.name}</span>
                        <span className={`text-[8px] font-bold ${tierCfg.color}`}>{vip.tierLabel}</span>
                      </div>
                      <p className="text-[9px] text-foreground-500">{vip.radioPrincipal} · {formatCount(vip.puntosTotales)} pts</p>
                    </div>
                    <div className={`${tierCfg.badgeBg} ${tierCfg.badgeText} px-2 py-0.5 rounded-full text-[8px] font-bold shrink-0`}>
                      {vip.score}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══ CLUBES ═══ */}
        {section === "clubes" && (
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-bold text-foreground-950">{clubs.length} Clubes</h3>
              <span className="text-[10px] text-foreground-400">{formatCount(126870)} miembros totales</span>
            </div>
            {topClubs.map((club) => {
              const typeCfg = clubTypeColors[club.type];
              return (
                <div key={club.id} className="bg-background-50 border border-background-200 rounded-2xl p-3">
                  <div className="flex items-start gap-2.5">
                    <div className="w-12 h-12 rounded-xl overflow-hidden bg-background-200 shrink-0">
                      <img src={club.parentLogoUrl} alt={club.clubName} className="w-full h-full object-cover object-top" loading="lazy" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <h4 className="text-xs font-bold text-foreground-900 truncate">{club.clubName}</h4>
                        <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${typeCfg.badge}`}>
                          <span className={typeCfg.text}>{clubTypeLabels[club.type]}</span>
                        </span>
                      </div>
                      <p className="text-[10px] text-foreground-500 mt-0.5 line-clamp-1">{club.slogan}</p>
                      <div className="flex items-center gap-3 mt-1.5">
                        <span className="text-[9px] text-foreground-600"><strong>{formatCount(club.membersCount)}</strong> miembros</span>
                        <span className="text-[9px] text-foreground-600"><strong>{club.clubScore}</strong> score</span>
                        {club.hasBadges && <span className="text-[8px] text-primary-500 font-semibold">Insignias</span>}
                        {club.hasEarlyAccess && <span className="text-[8px] text-accent-500 font-semibold">Early Access</span>}
                      </div>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-[9px] font-bold shrink-0 ${typeCfg.badge} ${typeCfg.text}`}>
                      #{club.rankingPosition}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}